import { FC, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import { useI18nValues } from '@/entities/vfo/hooks/useI18nValues';
import { Member } from '@/entities/vfo/types';
import { CaseIcon } from '@/features/vfo/components/CaseIcon';
import { useCases } from '@/features/vfo/hooks/useCases';
import { useChat } from '@/features/vfo/hooks/useChat';
import { useIsFounder } from '@/features/vfo/hooks/useIsFounder';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import {
  ApiChatResponse,
  ApiCreateAttachmentDto,
  ApiMessageResponse,
} from '@/shared/api/generated/data-contracts';
import { COLORS } from '@/shared/constants/colors';
import { getPersonFullName } from '@/shared/helpers/names';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { useBreakpoints } from '@/shared/hooks/useBreakpoints';
import { Icon } from '@/shared/uikit/Icon';
import { Button } from '@/shared/uikitV2/Button';
import { dates } from '@/shared/utils/dates';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';

import { ButtonDestructiveText } from '../ButtonDestructiveText';

import { MessageList } from './MessageList';
import {
  BackButton,
  Body,
  BodyHeader,
  BodyHeaderWrapper,
  BtnCloseStyled,
  ButtonAccent,
  CaseInfo,
  CaseTitle,
  ChatContent,
  ChatInputWrapper,
  EmptyCase,
  EmptyCaseText,
  EmptyCaseTitle,
  InfoWrapper,
  InfoWrapperText,
  NoChat,
  NoChatButton,
  NoChatButtons,
  NoChatTitle,
  Reply,
  ReplyClose,
  ReplyContent as ReplyContentStyled,
  ReplyName,
  ReplyText,
  Textarea,
  TextareaWrapper,
} from './styled';

type ChatBodyProps = {
  chatId: string | null;
  onClose: () => void;
  mobileView?: 'sidebar' | 'chat';
  onBack?: () => void;
};

const MAX_MESSAGE_LENGTH = 5000;

export const ChatBody: FC<ChatBodyProps> = ({ chatId, mobileView, onClose, onBack }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'vfoChat' });
  const [replyMessage, setReplyMessage] = useState<{
    id: string;
    text: string;
    user: {
      id: number;
      firstName?: string;
      lastName?: string;
    };
  } | null>(null);

  const modals = useModalsStore();
  const { membersWithoutCurrentUser } = useMembers();
  const { findByChatId } = useCases();
  const isFounder = useIsFounder();
  const { chats, unreadCounts, getMessages, sendMessage, markChatAsRead, deleteChat } = useChat();
  const { tag } = useI18nValues();
  const router = useAppRouter();
  const { md, sm } = useBreakpoints();

  const activeChat: ApiChatResponse | undefined = useMemo(() => {
    return chats.find((chat) => chat.id === chatId);
  }, [chats, chatId]);

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const previousScrollHeightRef = useRef<number | null>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const chatSelected = Boolean(activeChat);

  const messages = useMemo<ApiMessageResponse[]>(() => {
    const res: ApiMessageResponse[] = [];
    let lastDate: string | null = null;

    (activeChat?.messages ?? []).forEach((item) => {
      const itemDate = dates.formatDate(item.createdAt);
      if (itemDate !== lastDate) {
        res.push({
          id: `date-${itemDate}`,
          chatId: chatId ?? '',
          profileId: 0,
          messageType: 'TEXT',
          body: dates.formatDateSeparator(item.createdAt),
          isSystem: true,
          attachments: [],
          createdAt: item.createdAt,
          updatedAt: item.createdAt,
          messageReadMarks: [],
        });
        lastDate = itemDate;
      }
      res.push(item);
    });
    return res;
  }, [activeChat]);

  const isDirectMessage = activeChat?.chatType === 'DIRECT' || activeChat?.chatType === 'SELF';
  const caseData = chatId ? findByChatId(chatId) : undefined;
  const isCaseDeleted = !isDirectMessage && !caseData;
  const isGeneralCase = caseData?.isGeneralCase;
  const participants = caseData?.members ?? activeChat?.participants ?? [];

  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const scrollDown = () => {
    if (previousScrollHeightRef.current !== null) return;
    contentRef.current?.scrollTo({
      top: contentRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (previousScrollHeightRef.current !== null && contentRef.current) {
      const newScrollHeight = contentRef.current.scrollHeight;
      contentRef.current.scrollTop = newScrollHeight - previousScrollHeightRef.current;
      previousScrollHeightRef.current = null;
      return;
    }
    scrollDown();
  }, [messages]);

  const handleScroll = async (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    if (element.scrollTop === 0 && !isLoadingMore && hasMore && chatId) {
      setIsLoadingMore(true);
      previousScrollHeightRef.current = element.scrollHeight;

      const currentMessagesCount = activeChat?.messages.length || 0;

      const newMessages = await getMessages(chatId, {
        limit: 20,
        offset: currentMessagesCount,
      });

      if (!newMessages || newMessages.length < 20) {
        setHasMore(false);
      }

      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = 'auto'; // reset height
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  }, [message]);

  useEffect(() => {
    if (chatId && unreadCounts[chatId] !== 0) {
      markChatAsRead(chatId);
    }
  }, [chatId, markChatAsRead, unreadCounts]);

  const fetchMessages = async () => {
    if (!chatId) return;
    setHasMore(true);
    await getMessages(chatId, { limit: 20, offset: 0 });

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      scrollDown();
    }, 0);
  };

  useEffect(() => {
    fetchMessages();

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [chatId]);

  const handleSendMessage = async (msg: string, files?: ApiCreateAttachmentDto[]) => {
    if ((!msg && !files?.length) || !chatId || isSending) return;
    setIsSending(true);
    const arr: string[] = [];
    if (msg.length > MAX_MESSAGE_LENGTH) {
      for (let i = 0; i < msg.length; i += MAX_MESSAGE_LENGTH) {
        arr.push(msg.slice(i, i + MAX_MESSAGE_LENGTH));
      }
    } else {
      arr.push(msg);
    }

    for (let i = 0; i < arr.length; i++) {
      const item = arr[i];
      await sendMessage(chatId, {
        body: item || undefined,
        attachments: i === 0 ? files : undefined,
        replyToMessageId: replyMessage?.id,
        messageType: 'TEXT',
      });
    }

    setIsSending(false);
    setReplyMessage(null);
    setMessage('');
    scrollDown();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isSending) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(message.trim());
    }
  };

  const scrollToMessage = async (messageId: string) => {
    if (!chatId) return;
    const scroll = () => {
      const el = document.querySelector(`[data-messageId="${messageId}"`);
      el?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    };

    if (messages.find((item) => item.id === messageId)) {
      scroll();
      return;
    }

    setIsLoadingMore(true);

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const currentMessagesCount = activeChat?.messages.length || 0;

      const newMessages = await getMessages(chatId, {
        limit: 20,
        offset: currentMessagesCount,
      });

      if (newMessages?.find((item) => item.id === messageId)) {
        queueMicrotask(scroll);
        break;
      }

      if (!newMessages || newMessages.length < 20) {
        setHasMore(false);
        break;
      }
    }

    setIsLoadingMore(false);
  };

  const dmMember: Member | undefined = isDirectMessage
    ? membersWithoutCurrentUser.find((item) =>
        participants.some((p) => p.profileId === item.profileId),
      )
    : undefined;

  const hasMessages = messages.length > 0;
  const hasParticipants = participants.length > 1;
  const showNoChat = !chatSelected;
  const showEmptyCaseForFounder = chatSelected && !hasParticipants && !isGeneralCase && isFounder;
  const showGeneralCaseEmpty = chatSelected && !hasMessages && isGeneralCase;
  const showEmptyCaseForNonFounder = chatSelected && !hasMessages && !isGeneralCase && !isFounder;
  const showMessages = chatSelected && hasMessages;

  return (
    <Body className={cn({ mobileHidden: mobileView === 'sidebar' })}>
      {activeChat ? (
        <BodyHeaderWrapper>
          <BodyHeader
            onClick={() => {
              if (!activeChat) return;
              modals.openModal({
                type: 'VfoChatInfoModal',
                chat: activeChat,
                member: dmMember,
                onOpenCase: onClose,
              });
            }}
          >
            <CaseIcon caseData={caseData} member={isDirectMessage ? dmMember : undefined} />
            <CaseInfo>
              <CaseTitle>
                {isDirectMessage && dmMember
                  ? getPersonFullName(dmMember, 'display')
                  : activeChat?.title}
              </CaseTitle>
              {isDirectMessage && dmMember ? (
                <span>
                  {dmMember?.isFounder ? (
                    <>
                      <Icon name="medal" size="s12" /> {t('founder')}
                    </>
                  ) : (
                    dmMember?.tag && tag(dmMember?.tag)
                  )}
                </span>
              ) : (
                <span>
                  {participants.length} {participants.length === 1 ? t('member') : t('members')}
                </span>
              )}
            </CaseInfo>

            {!(md || sm) &&
              (!isCaseDeleted ? (
                <Button
                  style={{ marginLeft: 'auto' }}
                  type="text"
                  size="middle"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (caseData) {
                      router.push(`/vfo/case/${caseData?.id}`);
                    }
                    modals.closeModal();
                  }}
                >
                  {t('openCase')}
                </Button>
              ) : (
                <span style={{ color: COLORS.systemFlame200, marginLeft: 'auto' }}>
                  {t('caseDeleted')}
                </span>
              ))}

            <BackButton
              size="small"
              type="link"
              icon={<Icon name="arrow-left" size="s20" />}
              onClick={(e) => {
                e.stopPropagation();
                onBack?.();
              }}
            >
              {t('back')}
            </BackButton>
          </BodyHeader>
          <BtnCloseStyled onClick={onClose} size="s32" />
        </BodyHeaderWrapper>
      ) : (
        <BtnCloseStyled style={{ margin: '20px 15px 0 auto' }} onClick={onClose} />
      )}
      <ChatContent
        className={cn({
          empty:
            !isCaseDeleted && (!hasParticipants || !chatSelected || (!hasMessages && !isFounder)),
          isReply: replyMessage,
        })}
        ref={contentRef}
        onScroll={handleScroll}
      >
        {showNoChat && (
          <NoChat>
            <NoChatTitle>{t('vfoChat')}</NoChatTitle>
            {isFounder && (
              <>
                <div>
                  <div>{t('vfoChatInfo1')}</div>
                  <div>{t('vfoChatInfo2')}</div>
                </div>
                <NoChatButtons>
                  <NoChatButton>
                    <Icon name="users-three" size="s80" />
                    <Button
                      onClick={() => {
                        modals.openModal({
                          type: 'AddOrEditCaseModal',
                        });
                      }}
                      style={{ width: '100%' }}
                      size="small"
                      type="default"
                    >
                      {t('createCase')}
                    </Button>
                  </NoChatButton>
                  <NoChatButton>
                    <Icon name="user-circle" size="s80" />
                    <Button
                      onClick={() => {
                        modals.openModal({
                          type: 'InviteMemberModal',
                        });
                      }}
                      style={{ width: '100%' }}
                      size="small"
                      type="default"
                    >
                      {t('addNewMember')}
                    </Button>
                  </NoChatButton>
                </NoChatButtons>
              </>
            )}
          </NoChat>
        )}

        {showEmptyCaseForFounder && !isCaseDeleted && (
          <EmptyCase>
            <Icon name="wechat-logo" size="s80" style={{ color: COLORS.systemDark100 }} />
            <EmptyCaseText>
              <EmptyCaseTitle>
                {t('youAreInCaseChat', { caseName: activeChat?.title ?? '' })}
              </EmptyCaseTitle>
              <span>{t('addMember')}</span>
            </EmptyCaseText>
            <Button
              type="default"
              onClick={() => {
                if (caseData) {
                  modals.openModal({
                    type: 'AddMembersToCaseModal',
                    caseId: caseData?.id,
                  });
                }
              }}
            >
              {t('addMemberBtn')}
            </Button>
          </EmptyCase>
        )}

        {showGeneralCaseEmpty && !isCaseDeleted && (
          <EmptyCase>
            <Icon name="wechat-logo" size="s80" style={{ color: COLORS.systemDark100 }} />
            <EmptyCaseText>
              <EmptyCaseTitle>{t('generalCaseTitle')}</EmptyCaseTitle>
              <span>{t('generalCaseInfo')}</span>
            </EmptyCaseText>
          </EmptyCase>
        )}

        {showEmptyCaseForNonFounder && !isCaseDeleted && (
          <EmptyCase>
            <Icon name="wechat-logo" size="s80" style={{ color: COLORS.systemDark100 }} />
            <EmptyCaseText>
              <EmptyCaseTitle>{t('noMessagesYet')}</EmptyCaseTitle>
            </EmptyCaseText>
          </EmptyCase>
        )}

        {showMessages && (
          <MessageList
            scrollToMessage={scrollToMessage}
            messages={messages}
            caseData={caseData}
            setReplyMessage={setReplyMessage}
            isCaseDeleted={isCaseDeleted}
          />
        )}
      </ChatContent>

      {!showNoChat && !isCaseDeleted && (
        <>
          <ChatInputWrapper htmlFor="textarea">
            {replyMessage && (
              <Reply>
                <ReplyContentStyled>
                  <ReplyName>{getPersonFullName(replyMessage.user, 'display')}</ReplyName>
                  <ReplyText>{replyMessage.text}</ReplyText>
                </ReplyContentStyled>
                <ReplyClose onClick={() => setReplyMessage(null)}>
                  <Icon name="x" size="s20" />
                </ReplyClose>
              </Reply>
            )}
            <TextareaWrapper>
              <Button
                icon={<Icon name="paperclip" size="s24" />}
                type="link"
                size="small"
                style={{ color: COLORS.systemDark200 }}
                onClick={() => {
                  modals.openModal({
                    type: 'UploadVfoDocumentModal',
                    isChat: true,
                    caseId: caseData?.id,
                    message,
                    onFinish: (_, formValues) => {
                      handleSendMessage(
                        (formValues?.message ?? '').trim(),
                        formValues?.files
                          .filter((item) => item.document)
                          ?.map((item) => ({
                            fileName: item.document?.name ?? item.title ?? '',
                            fileSizeBytes: item.document?.size ?? 0,
                            mimeType: item.document?.type ?? '',
                            title: item.title ?? '',
                            filePath: item.filePath,
                          })),
                      );
                    },
                  });
                }}
              />
              <Textarea
                id="textarea"
                ref={textareaRef}
                rows={1}
                placeholder={t('typeSomething')}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                disabled={!chatSelected || participants.length === 0}
              />
              <ButtonAccent
                disabled={!message.length || isSending}
                icon={<Icon name="paper-plane-right" size="s20" />}
                onClick={() => handleSendMessage(message.trim())}
              />
            </TextareaWrapper>
          </ChatInputWrapper>
        </>
      )}

      {!showNoChat && isCaseDeleted && (
        <InfoWrapper>
          <InfoWrapperText>{t('caseDeletedInfoPanel')}</InfoWrapperText>
          {isFounder && (
            <ButtonDestructiveText
              type="text"
              size="small"
              style={{ height: 20 }}
              onClick={() => {
                if (!chatId) return;
                modals.openModal({
                  type: 'ConfirmModalV2',
                  variant: 'delete',
                  title: t('deleteConfirmTitle'),
                  bodyText: t('deleteConfirmText'),
                  confirmText: t('deleteHistoryBtn'),
                  onConfirm: () => deleteChat(chatId),
                });
              }}
            >
              {t('deleteHistoryBtn')}
            </ButtonDestructiveText>
          )}
        </InfoWrapper>
      )}
    </Body>
  );
};
